import { Database } from "@/types/supabase-types";
import { supabase } from ".";

export type SupabaseRpcArgs<TRpcName extends keyof Database["public"]["Functions"]> =
  Database["public"]["Functions"][TRpcName]["Args"] & { pageLimit?: number };
export type SupabaseRpcReturn<TRpcName extends keyof Database["public"]["Functions"]> =
  Database["public"]["Functions"][TRpcName]["Returns"];

export interface PageableRpcParams {
  pageLimit?: number;
  pageOffset?: number;
}

export interface PageableRpcReturn<TData> {
  data: TData[];
  count: number;
}

export type PageableRpcName = {
  [RpcName in keyof Database["public"]["Functions"]]: SupabaseRpcArgs<RpcName> extends PageableRpcParams ?
    SupabaseRpcReturn<RpcName> extends PageableRpcReturn<infer _TData> ?
      RpcName
    : never
  : never;
}[keyof Database["public"]["Functions"]];

export type PageableRpcReturnData<TRpcName extends PageableRpcName> = SupabaseRpcReturn<TRpcName>["data"][number];

/**
 * Call a Supabase RPC that supports paging. In order to support paging, an RPC function must `pageLimit` and `pageOffset`
 * parameters to control paging, and return a JSON object with format { data, count }. These requirements are automatically
 * enforced with Typescript for the current database.
 *
 * @param rpcName The name of the RPC function. Must be an RPC that supports paging.
 * @param params The parameters for the RPC, must include `pageLimit` and `pageOffset`.
 * @param pageSize Optional page size (default: 1000)
 * @returns The data from all pages, aggregated into a single array.
 */
export async function supabasePagedRPC<TRpcName extends PageableRpcName>(
  rpcName: TRpcName,
  params: Omit<SupabaseRpcArgs<TRpcName>, "pageOffset"> & PageableRpcParams,
): Promise<PageableRpcReturnData<TRpcName>[]> {
  const allData = [] as PageableRpcReturnData<TRpcName>[];
  let pageOffset = 0;
  const pageLimit = params.pageLimit ?? 1000;

  while (true) {
    // Fetch the current page
    const rpcParams = { ...params, pageLimit, pageOffset } as SupabaseRpcArgs<TRpcName>;
    const response = (await supabase.rpc(rpcName, rpcParams).throwOnError()).data;

    // Add the data to the results
    const responseData = response.data;
    allData.push(...responseData);

    // Exit if this is the last page
    if (responseData.length < pageLimit) break;
    if (allData.length >= response.count) break;

    // Increment the offset for the next page
    pageOffset += pageLimit;
  }

  return allData;
}
